import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  TextField,
  Alert as MuiAlert,
  Tooltip,
  TableSortLabel,
  CircularProgress,
  OutlinedInput,
  ListItemText,
  Checkbox,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { filterPanelSx } from "../../components/common/filterStyles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import PageMeta from "../../components/common/PageMeta";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { CmLink } from "../../components/common/CmLink";
import { formatCurrency } from "../../utils/formatCurrency";
import { buildProviderAlertLink } from "../../utils/alertLogLinks";
import { pascalCase } from "../../utils/textUtils";
import { RefundLogStatus } from "../../types/autoRefund";
import { logStatusColors } from "./ActivityLog";
import {
  AlertLogFilter,
  AlertLogFilterOptions,
  AlertLogRow,
  AlertLogSearchRequest,
  AlertLogSearchResponse,
} from "../../types/alertLogs";

type EnumField =
  | "pp_type"
  | "alert_type"
  | "alert_source"
  | "alert_request"
  | "alert_resolve_status"
  | "alert_resolve_refunded"
  | "match_method"
  | "refund_request"
  | "status";

type EnumOrTriggerField = EnumField | "trigger_source";

const ENUM_FIELDS: EnumOrTriggerField[] = [
  "pp_type",
  "alert_type",
  "alert_source",
  "alert_request",
  "alert_resolve_status",
  "alert_resolve_refunded",
  "match_method",
  "refund_request",
  "status",
  "trigger_source",
];

type TextFieldKey =
  | "provider_alert_key"
  | "descriptor_name"
  | "merchant_name"
  | "matched_order_name"
  | "user_email"
  | "notes"
  | "error_message"
  | "alert_currency"
  | "refund_currency";

const SORTABLE_FIELDS = new Set([
  "created_at",
  "alert_amount",
  "refund_amount",
  "status",
  "alert_source",
  "trigger_source",
  "dry_run",
  "provider_alert_key",
]);

const DEFAULT_LIMIT = 50;

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "--";
  const s = value instanceof Date ? value.toISOString() : value;
  return dayjs.utc(s).format("MMM DD, YYYY HH:mm");
}

function truncate(text: string, max = 40): string {
  return text.length <= max ? text : text.slice(0, max) + "...";
}

interface ColumnFilters {
  enums: Partial<Record<EnumOrTriggerField, string[]>>;
  texts: Partial<Record<TextFieldKey, string>>;
  amountMin: { alert_amount?: string; refund_amount?: string };
  amountMax: { alert_amount?: string; refund_amount?: string };
  dryRun?: boolean;
}

const emptyColumnFilters: ColumnFilters = {
  enums: {},
  texts: {},
  amountMin: {},
  amountMax: {},
};

function parseColumnFilters(raw: string | null): ColumnFilters {
  if (!raw) return emptyColumnFilters;
  try {
    const parsed = JSON.parse(raw);
    return {
      enums: parsed.enums ?? {},
      texts: parsed.texts ?? {},
      amountMin: parsed.amountMin ?? {},
      amountMax: parsed.amountMax ?? {},
      dryRun: typeof parsed.dryRun === "boolean" ? parsed.dryRun : undefined,
    };
  } catch {
    return emptyColumnFilters;
  }
}

function serializeColumnFilters(filters: ColumnFilters): string | null {
  const empty =
    Object.keys(filters.enums).length === 0 &&
    Object.keys(filters.texts).length === 0 &&
    Object.keys(filters.amountMin).length === 0 &&
    Object.keys(filters.amountMax).length === 0 &&
    filters.dryRun === undefined;
  return empty ? null : JSON.stringify(filters);
}

function buildApiFilters(c: ColumnFilters): AlertLogFilter[] {
  const out: AlertLogFilter[] = [];
  if (c.dryRun !== undefined) {
    out.push({ field: "dry_run", operator: "eq", value: c.dryRun });
  }
  for (const [field, values] of Object.entries(c.enums)) {
    if (values && values.length > 0) {
      out.push({ field, operator: "in", value: values });
    }
  }
  for (const [field, value] of Object.entries(c.texts)) {
    if (value && value.trim() !== "") {
      out.push({ field, operator: "contains", value: value.trim() });
    }
  }
  for (const [field, v] of Object.entries(c.amountMin)) {
    if (v && v !== "") {
      const dollars = parseFloat(v);
      if (!Number.isNaN(dollars)) {
        out.push({ field, operator: "gte", value: Math.round(dollars * 100) });
      }
    }
  }
  for (const [field, v] of Object.entries(c.amountMax)) {
    if (v && v !== "") {
      const dollars = parseFloat(v);
      if (!Number.isNaN(dollars)) {
        out.push({ field, operator: "lte", value: Math.round(dollars * 100) });
      }
    }
  }
  return out;
}

export default function AlertLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyId } = useCompanyId();
  const { post, get } = useApi();

  const startDate = searchParams.get("startDate") || dayjs.utc().subtract(3, "day").format("YYYY-MM-DD");
  const endDate = searchParams.get("endDate") || dayjs.utc().format("YYYY-MM-DD");
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || DEFAULT_LIMIT);
  const sortBy = searchParams.get("sortBy") || "created_at";
  const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";
  const filtersParam = searchParams.get("filters");
  const columnFilters = useMemo(() => parseColumnFilters(filtersParam), [filtersParam]);

  const [data, setData] = useState<AlertLogRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<AlertLogFilterOptions>({});
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const [localTexts, setLocalTexts] = useState<Partial<Record<TextFieldKey, string>>>(columnFilters.texts);
  const [localAmountMin, setLocalAmountMin] = useState(columnFilters.amountMin);
  const [localAmountMax, setLocalAmountMax] = useState(columnFilters.amountMax);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    setLocalTexts(columnFilters.texts);
    setLocalAmountMin(columnFilters.amountMin);
    setLocalAmountMax(columnFilters.amountMax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersParam]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const updateColumnFilters = useCallback(
    (next: ColumnFilters) => {
      updateParams({ filters: serializeColumnFilters(next), page: "1" });
    },
    [updateParams]
  );

  useEffect(() => {
    get<AlertLogFilterOptions>("/api/alert-logs/filter-options")
      .then(setFilterOptions)
      .catch(() => setFilterOptions({}));
  }, [get]);

  useEffect(() => {
    const body: AlertLogSearchRequest = {
      startDate,
      endDate,
      filters: buildApiFilters(columnFilters),
      sortBy,
      sortOrder,
      page,
      limit,
      ...(companyId ? { companyId } : {}),
    };

    setLoading(true);
    setError(null);
    post<AlertLogSearchResponse>("/api/alert-logs/search", body, { slowApi: true })
      .then((res) => {
        setData(res.data);
        setTotalItems(res.pagination.totalItems);
      })
      .catch((err) => setError(err?.message ?? "Failed to load alert logs"))
      .finally(() => setLoading(false));
  }, [
    post,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    page,
    limit,
    companyId,
    columnFilters,
  ]);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next: ColumnFilters = {
        enums: columnFilters.enums,
        texts: localTexts,
        amountMin: localAmountMin,
        amountMax: localAmountMax,
        dryRun: columnFilters.dryRun,
      };
      const serialized = serializeColumnFilters(next);
      if (serialized !== filtersParam) {
        updateColumnFilters(next);
      }
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTexts, localAmountMin, localAmountMax]);

  const handleSort = (field: string) => {
    if (!SORTABLE_FIELDS.has(field)) return;
    if (sortBy === field) {
      updateParams({ sortOrder: sortOrder === "asc" ? "desc" : "asc" });
    } else {
      updateParams({ sortBy: field, sortOrder: "desc" });
    }
  };

  const handleEnumChange = (field: EnumOrTriggerField, values: string[]) => {
    const nextEnums = { ...columnFilters.enums };
    if (values.length === 0) delete nextEnums[field];
    else nextEnums[field] = values;
    updateColumnFilters({ ...columnFilters, enums: nextEnums });
  };

  const handleDryRunChange = (v: string) => {
    const next: ColumnFilters = { ...columnFilters };
    if (v === "all") next.dryRun = undefined;
    else next.dryRun = v === "true";
    updateColumnFilters(next);
  };

  const setText = (field: TextFieldKey, v: string) => {
    const next = { ...localTexts };
    if (v === "") delete next[field];
    else next[field] = v;
    setLocalTexts(next);
  };

  const isColumnActive = (key: string): boolean => {
    if (key === "dry_run") return columnFilters.dryRun !== undefined;
    if (ENUM_FIELDS.includes(key as EnumOrTriggerField)) {
      return (columnFilters.enums[key as EnumOrTriggerField]?.length ?? 0) > 0;
    }
    if (key === "alert_amount" || key === "refund_amount") {
      return (
        !!columnFilters.amountMin[key as "alert_amount" | "refund_amount"] ||
        !!columnFilters.amountMax[key as "alert_amount" | "refund_amount"]
      );
    }
    return !!columnFilters.texts[key as TextFieldKey];
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (columnFilters.dryRun !== undefined) n++;
    n += Object.values(columnFilters.enums).filter((v) => v && v.length > 0).length;
    n += Object.values(columnFilters.texts).filter((v) => v && v.trim() !== "").length;
    n += Object.values(columnFilters.amountMin).filter((v) => v && v !== "").length;
    n += Object.values(columnFilters.amountMax).filter((v) => v && v !== "").length;
    return n;
  }, [columnFilters]);

  const clearAllColumnFilters = () => updateColumnFilters(emptyColumnFilters);

  const statusOptions = useMemo(
    () => (filterOptions.status || []).map((v) => ({ value: v, label: pascalCase(v) })),
    [filterOptions.status]
  );

  const columns: Array<{ key: string; label: string; sortable?: boolean }> = [
    { key: "provider_alert_key", label: "Alert ID", sortable: true },
    { key: "alert_source", label: "Source", sortable: true },
    { key: "trigger_source", label: "Trigger", sortable: true },
    { key: "dry_run", label: "Dry Run", sortable: true },
    { key: "pp_account", label: "Account" },
    { key: "merchant_name", label: "Merchant" },
    { key: "descriptor_name", label: "Descriptor" },
    { key: "alert_type", label: "Alert Type" },
    { key: "alert_amount", label: "Alert Amount", sortable: true },
    { key: "alert_currency", label: "Currency" },
    { key: "alert_request", label: "Alert Req" },
    { key: "refund_amount", label: "Refund Amount", sortable: true },
    { key: "refund_currency", label: "Refund Ccy" },
    { key: "refund_request", label: "Refund Req" },
    { key: "alert_resolve_status", label: "Resolve Status" },
    { key: "alert_resolve_refunded", label: "Resolve Refunded" },
    { key: "match_method", label: "Match" },
    { key: "matched_order_name", label: "Order" },
    { key: "status", label: "Status", sortable: true },
    { key: "error_message", label: "Error" },
    { key: "user_email", label: "Email" },
    { key: "notes", label: "Notes" },
  ];

  
  const renderFilterCell = (key: string) => {
    if (key === "dry_run") {
      const v = columnFilters.dryRun === undefined ? "all" : columnFilters.dryRun ? "true" : "false";
      return (
        <FormControl size="small" fullWidth>
          <Select
            value={v}
            onChange={(e) => handleDryRunChange(e.target.value)}
            sx={{ fontSize: "0.75rem", minWidth: 90 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (key === "status") {
      return (
        <SearchableMultiSelectFilter
          options={statusOptions}
          value={columnFilters.enums.status || []}
          onChange={(values) => handleEnumChange("status", values)}
          placeholder=""
          searchPlaceholder="Search status…"
          width={120}
          dense
          showSelectAllClearAll
        />
      );
    }

    if (ENUM_FIELDS.includes(key as EnumOrTriggerField)) {
      const selected = columnFilters.enums[key as EnumOrTriggerField] || [];
      const options = filterOptions[key] || [];
      return (
        <FormControl size="small" fullWidth>
          <Select<string[]>
            multiple
            value={selected}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const v = e.target.value;
              handleEnumChange(key as EnumOrTriggerField, typeof v === "string" ? v.split(",") : v);
            }}
            input={<OutlinedInput />}
            renderValue={(s) => s.map(pascalCase).join(", ")}
            displayEmpty
            sx={{ fontSize: "0.75rem", minWidth: 120 }}
          >
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={selected.includes(opt)} size="small" />
                <ListItemText primary={pascalCase(opt)} primaryTypographyProps={{ fontSize: "0.8rem" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (key === "alert_amount" || key === "refund_amount") {
      const amountKey = key as "alert_amount" | "refund_amount";
      const minVal = localAmountMin[amountKey] ?? "";
      const maxVal = localAmountMax[amountKey] ?? "";
      return (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <TextField
            size="small"
            placeholder="min"
            value={minVal}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...localAmountMin };
              if (v === "") delete next[amountKey];
              else next[amountKey] = v;
              setLocalAmountMin(next);
            }}
            sx={{ width: 60 }}
            inputProps={{ style: { fontSize: "0.75rem", padding: "4px 6px" } }}
          />
          <TextField
            size="small"
            placeholder="max"
            value={maxVal}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...localAmountMax };
              if (v === "") delete next[amountKey];
              else next[amountKey] = v;
              setLocalAmountMax(next);
            }}
            sx={{ width: 60 }}
            inputProps={{ style: { fontSize: "0.75rem", padding: "4px 6px" } }}
          />
        </Box>
      );
    }

    if (key === "pp_account") {
      return null;
    }

    const textKey = key as TextFieldKey;
    return (
      <TextField
        size="small"
        placeholder="filter"
        value={localTexts[textKey] ?? ""}
        onChange={(e) => setText(textKey, e.target.value)}
        fullWidth
        inputProps={{ style: { fontSize: "0.75rem", padding: "4px 6px" } }}
      />
    );
  };

  const renderCell = (row: AlertLogRow, key: string) => {
    switch (key) {
      case "provider_alert_key": {
        const k = row.provider_alert_key || "";
        if (!k) return "--";
        return (
          <CmLink
            to={buildProviderAlertLink(row)}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#465fff" }}
          >
            {k}
          </CmLink>
        );
      }
      case "pp_account": {
        const pp = row.pp_type ? pascalCase(row.pp_type) : "--";
        const name = row.pp_account_name || row.pp_account_shop;
        return name ? `${pp} — ${name}` : pp;
      }
      case "alert_amount":
        return row.alert_amount != null && row.alert_currency
          ? formatCurrency(row.alert_amount, row.alert_currency)
          : row.alert_amount != null
            ? String(row.alert_amount / 100)
            : "--";
      case "refund_amount":
        return row.refund_amount != null && row.refund_currency
          ? formatCurrency(row.refund_amount, row.refund_currency)
          : row.refund_amount != null
            ? String(row.refund_amount / 100)
            : "--";
      case "matched_order_name":
        if (!row.matched_order_name) return "--";
        return row.matched_order_url ? (
          <a
            href={row.matched_order_url}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#465fff", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            {row.matched_order_name}
            <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
          </a>
        ) : (
          row.matched_order_name
        );
      case "dry_run":
        return (
          <Chip
            label={row.dry_run ? "Yes" : "No"}
            size="small"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
        );
      case "status": {
        const colors = logStatusColors[row.status as RefundLogStatus] || { main: "#6b7280", bg: "#f3f4f6" };
        return (
          <Chip
            label={pascalCase(row.status)}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.main,
              fontWeight: 500,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
        );
      }
      case "error_message":
      case "notes": {
        const v = (row as unknown as Record<string, unknown>)[key] as string | null;
        if (!v) return "--";
        const t = truncate(v, 30);
        return v.length > 30 ? (
          <Tooltip title={v}>
            <span>{t}</span>
          </Tooltip>
        ) : (
          v
        );
      }
      case "alert_type":
      case "pp_type":
      case "alert_source":
      case "alert_request":
      case "alert_resolve_status":
      case "alert_resolve_refunded":
      case "match_method":
      case "refund_request":
      case "trigger_source": {
        const v = (row as unknown as Record<string, unknown>)[key];
        return v ? pascalCase(String(v)) : "--";
      }
      default: {
        const v = (row as unknown as Record<string, unknown>)[key];
        return v == null || v === "" ? "--" : String(v);
      }
    }
  };

  return (
    <>
      <PageMeta title="Alert Logs | Chargemont" description="Audit auto + manual refund activity" />
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Alert Logs
        </Typography>

        <Box sx={[filterPanelSx, { p: 2, mb: 2 }]}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <DatePicker
                label="Start Date"
                value={dayjs.utc(startDate)}
                onChange={(d: Dayjs | null) =>
                  d && updateParams({ startDate: d.format("YYYY-MM-DD"), page: "1" })
                }
                open={startOpen}
                onOpen={() => setStartOpen(true)}
                onClose={() => setStartOpen(false)}
                slotProps={{ textField: { size: "small", onClick: () => setStartOpen(true) } }}
              />
              <DatePicker
                label="End Date"
                value={dayjs.utc(endDate)}
                onChange={(d: Dayjs | null) =>
                  d && updateParams({ endDate: d.format("YYYY-MM-DD"), page: "1" })
                }
                open={endOpen}
                onOpen={() => setEndOpen(true)}
                onClose={() => setEndOpen(false)}
                slotProps={{ textField: { size: "small", onClick: () => setEndOpen(true) } }}
              />
              {loading && <CircularProgress size={20} />}
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} active`}
                  size="small"
                  onDelete={clearAllColumnFilters}
                  sx={{
                    backgroundColor: "#ecf3ff",
                    color: "#4338ca",
                    fontWeight: 600,
                    border: "1px solid #c7d2fe",
                  }}
                />
              )}
            </Box>
          </LocalizationProvider>
        </Box>

        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        <Paper>
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "created_at"}
                      direction={sortBy === "created_at" ? sortOrder : "desc"}
                      onClick={() => handleSort("created_at")}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  {columns.map((col) => {
                    const active = isColumnActive(col.key);
                    return (
                      <TableCell
                        key={col.key}
                        sx={active ? { fontWeight: 700, color: "#4338ca" } : undefined}
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={sortBy === col.key}
                            direction={sortBy === col.key ? sortOrder : "desc"}
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell />
                  {columns.map((col) => {
                    const active = isColumnActive(col.key);
                    return (
                      <TableCell
                        key={col.key + "-filter"}
                        sx={
                          active
                            ? {
                                backgroundColor: "#ecf3ff",
                                borderLeft: "2px solid #465fff",
                              }
                            : undefined
                        }
                      >
                        {renderFilterCell(col.key)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      <Typography variant="body2" sx={{ py: 3, color: "#6b7280" }}>
                        No logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.log_id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                        {formatDateTime(row.created_at)}
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell key={col.key} sx={{ fontSize: "0.75rem" }}>
                          {renderCell(row, col.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={Math.max(0, page - 1)}
            onPageChange={(_e, newPage) => updateParams({ page: String(newPage + 1) })}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => updateParams({ limit: e.target.value, page: "1" })}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>
      </Box>
    </>
  );
}
